import { MARGIN_LEFT, MARGIN_RIGHT } from '../../utils/pdfConstants';
import { getPoranaytteetByTarkastuskohdeV2 } from '../../../api/api';

// v2 counterpart of RenderPoranayteBlock.js. Styling/layout code below is a
// deliberate copy of that file's constants (not imported) — v1's PDF output
// stays completely untouched. The only real difference from v1 is how rows
// are sourced: v1 reads pre-fetched mittausCache entries synchronously; v2
// fetches live from the tarkastuskohde-scoped poränäyte endpoint (this
// block's DB-backed table has no embedded JSON of its own to read).
// v2 also needs no karb_per_nayte/karbSettings override indirection — each
// poranayte row is the single source of truth for its own karb values.

// A4 landscape content width
const LANDSCAPE_W = Math.round(841.89 - MARGIN_LEFT - MARGIN_RIGHT); // ~785pt (unused but documents the budget)

const GREEN = '#04aa00';
const GREEN2 = '#038800';
const F = { font: 'Lato', fontSize: 7 };
const TH = { ...F, bold: true, color: '#fff', fillColor: GREEN, alignment: 'center', verticalAlignment: 'middle' };
const TH2 = { ...TH, fillColor: GREEN2 };
const TD = (opts = {}) => ({ ...F, alignment: 'center', verticalAlignment: 'middle', ...opts });
const E = { text: '', ...F, verticalAlignment: 'middle' };

function v(val) { return (val !== null && val !== undefined && val !== '') ? String(val) : '—'; }

// karb_yp_*/karb_ap_* are DECIMAL(6,1) columns — Sequelize returns them as
// fixed-precision strings ("11.0", "0.0") — strip the trailing .0 for
// display, same fix already applied in PoranayteView.jsx for the live table.
function vNum(val) {
  if (val === null || val === undefined || val === '') return '—';
  const n = parseFloat(val);
  return Number.isNaN(n) ? String(val) : String(n);
}

const tableLayout = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => '#adb5bd',
  vLineColor: () => '#adb5bd',
  paddingLeft:   () => 3,
  paddingRight:  () => 3,
  paddingTop:    () => 5,
  paddingBottom: () => 2,
  fillColor: (row) => row > 2 && row % 2 === 0 ? '#f8f9fa' : null,
};

const headerRows = [
  [
    { text: 'Näyte-\ntunnus',                   ...TH, rowSpan: 3 },
    { text: 'Rakenneosa',                        ...TH, rowSpan: 3 },
    { text: 'Testi',                             ...TH, rowSpan: 3 },
    { text: 'Testaus-\nsyvyys\npinta/mm',        ...TH, rowSpan: 3 },
    { text: 'Näytteen\nØ mm',                    ...TH, rowSpan: 3 },
    { text: 'Teräksen\nsijainti\nmm (Ø mm)',     ...TH, rowSpan: 3 },
    { text: 'Karbonatisoitumissyvyys mm',        ...TH, colSpan: 6 }, E, E, E, E, E,
    { text: 'Lisähuomioita',                     ...TH, rowSpan: 3 },
    { text: 'Rakenne-\npaksuus\nmm',             ...TH, rowSpan: 3 },
  ],
  [
    E, E, E, E, E, E,
    { text: 'up/yp/rp0', ...TH2, colSpan: 3 }, E, E,
    { text: 'sp/ap/rp1', ...TH2, colSpan: 3 }, E, E,
    E, E,
  ],
  [
    E, E, E, E, E, E,
    { text: 'min', ...TH2 }, { text: 'max', ...TH2 }, { text: 'k-m', ...TH2 },
    { text: 'min', ...TH2 }, { text: 'max', ...TH2 }, { text: 'k-m', ...TH2 },
    E, E,
  ],
];

const DATA_FIELDS = ['nayte_tunnus', 'rakenneosa', 'testi', 'testaussyvyys', 'naytteen_pinta',
  'terakseen_sijainti', 'lisahuomioita', 'rakennepaksuus'];

function hasData(nayte) {
  return DATA_FIELDS.some(f => nayte[f] !== '' && nayte[f] != null);
}

export async function renderPoranayteBlockV2({ block, content, tableCounter }) {
  const nav = block?.data?.nav ?? {};
  if (!nav.tkId) return;

  let raw;
  try { raw = (await getPoranaytteetByTarkastuskohdeV2(nav.tkId)).data; }
  catch (e) { console.warn('[RenderPoranayteBlockV2] failed to fetch poränäytteet:', e); return; }

  const rows = (raw ?? [])
    .map(n => ({ ...n, rakenneosa: n.rakenneosa?.nimi ?? '' }))
    .filter(hasData)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  if (!rows.length) return;

  const tblNum = tableCounter ? ++tableCounter.value : null;

  const dataRows = rows.map(n => [
    TD({ text: v(n.nayte_tunnus) }),
    TD({ text: v(n.rakenneosa) }),
    TD({ text: v(n.testi) }),
    TD({ text: v(n.testaussyvyys) }),
    TD({ text: v(n.naytteen_pinta) }),
    TD({ text: v(n.terakseen_sijainti) }),
    TD({ text: vNum(n.karb_yp_min) }),
    TD({ text: vNum(n.karb_yp_max) }),
    TD({ text: vNum(n.karb_yp_km) }),
    TD({ text: vNum(n.karb_ap_min) }),
    TD({ text: vNum(n.karb_ap_max) }),
    TD({ text: vNum(n.karb_ap_km) }),
    TD({ text: v(n.lisahuomioita), alignment: 'left' }),
    TD({ text: v(n.rakennepaksuus) }),
  ]);

  const isLandscape = !!block?.data?.landscapePage;

  const tableNode = {
    table: {
      headerRows: 3,
      widths: isLandscape
        ? [30, 70, 34, 30, 24, 28, 18, 18, 20, 18, 18, 20, '*', 60]
        : [24, 50, 28, 28, 22, 26, 15, 15, 15, 15, 15, 15, '*', 56],
      body: [...headerRows, ...dataRows],
    },
    layout: tableLayout,
    margin: [0, 5, 0, 0],
  };

  // No fallback — an empty caption stays empty in the PDF. The table
  // number still always shows.
  const caption = block?.data?.caption;
  const captionText = (typeof caption === 'string' ? caption : caption?.value ?? '').trim();
  const captionNode = tblNum !== null ? {
    text: captionText
      ? [{ text: `Taulukko ${tblNum}. `, bold: true }, { text: captionText, bold: false }]
      : [{ text: `Taulukko ${tblNum}.`, bold: true }],
    font: 'Lato', fontSize: 9,
    margin: [0, 4, 0, 0],
  } : null;

  const stackItems = [tableNode, ...(captionNode ? [captionNode] : [])];

  if (isLandscape) {
    content.push({
      pageBreak: 'before',
      pageOrientation: 'landscape',
      stack: stackItems,
      margin: [0, 0, 0, 15],
    });
    // Switch back to portrait for content that follows
    content.push({ text: '', pageBreak: 'before', pageOrientation: 'portrait' });
  } else {
    content.push({
      stack: stackItems,
      margin: [0, 0, 0, 15],
    });
  }
}
